import { useEffect, useMemo, useState } from "react"
import {
  LabelMedium,
  ModalBody,
  ModalFooter,
  ParagraphSmall,
  useStyletron,
} from "@mezo-org/mezo-clay"
import { useQueryClient } from "@tanstack/react-query"
import { useAccount } from "wagmi"

import { RootState, selectTokenDecimals, useDappSelector } from "#/redux-state"
import FailedActionModalBody from "#/shared/components/modals/FailedActionModalBody"
import WaitingForActionModalBody from "#/shared/components/modals/WaitingForActionModalBody"
import ProgressSteps, { NumberedStep } from "#/shared/components/ProgressSteps"
import { queryKeysFactory } from "#/shared/constants"
import { address as matsnetBTCAddress } from "#/shared/constants/artifacts/MatsnetBTC"
import { address as matsnetVeBTCAddress } from "#/shared/constants/artifacts/VeBTC"
import {
  useApproveERC20Token,
  useBTCToUSDConversion,
  useEstimateVeBTCValue,
  useIncreaseStakeAmount,
  useStake,
} from "#/shared/hooks"
import { Stake, STAKING_LOCK_DURATION } from "#/shared/types"
import {
  bigIntToFloat,
  bigIntToHumanReadableFormat,
  floatToBigInt,
  networkUtils,
  separateThousandsWithComma,
} from "#/shared/utils"
import {
  ComponentStepProps,
  SignTransactionModalButton,
  useStepperModalContext,
} from "#/ui/Modal"

import {
  ReviewDetails,
  ReviewDetailsListItem,
  StakingRewardsLabel,
} from "../../components"
import { useManageStakeFlowContext } from "../ManageStakeFlowContext"

export default function ReviewIncreasedAmount({
  onClickContinue,
}: ComponentStepProps) {
  const [css, theme] = useStyletron()

  const tokenDecimals = useDappSelector((state: RootState) =>
    selectTokenDecimals(state, "mBTC"),
  )

  const [step, setStep] = useState(0)

  const {
    approvedAmount,
    approve,
    isFetchingApprovedAmount,
    isPending: isApprovalTxPending,
    isTxConfirmed: isApproveTxConfirmed,
    isWaitingForTxReceipt: isWaitingForApproveTxReceipt,
  } = useApproveERC20Token(matsnetBTCAddress, matsnetVeBTCAddress)

  const {
    increaseAmount,
    isPending: isIncreaseAmountTxPending,
    isError,
    isWaitingForTxReceipt: isWaitingForIncreaseAmountTxReceipt,
    isTxConfirmed: isIncreaseAmountTxConfirmed,
  } = useIncreaseStakeAmount()

  const {
    values: { amount: increasedAmount, stakeId },
  } = useStepperModalContext<{
    amount: string
    stakeId: string
  }>()

  const increasedAmountToStake = floatToBigInt(increasedAmount)

  const { manageType } = useManageStakeFlowContext()
  const { address } = useAccount()

  const queryClient = useQueryClient()

  useEffect(() => {
    if (!isIncreaseAmountTxConfirmed) return

    queryClient.setQueryData<Stake[]>(
      [...queryKeysFactory.userKeys.stakes(), address],
      (prevData) => {
        if (!prevData) return []

        const stakeToUpdateIndex = prevData.findIndex(
          (stake) => stake.id === stakeId,
        )

        if (stakeToUpdateIndex < 0) return prevData
        const stakeToUpdate = prevData[stakeToUpdateIndex]

        const updatedStakes = [...prevData]
        updatedStakes[stakeToUpdateIndex] = {
          ...stakeToUpdate,
          amount: stakeToUpdate.amount + increasedAmountToStake,
        }

        return updatedStakes
      },
    )
    onClickContinue()
  }, [
    isIncreaseAmountTxConfirmed,
    onClickContinue,
    queryClient,
    address,
    stakeId,
    manageType,
    increasedAmountToStake,
  ])

  useEffect(() => {
    if (
      approvedAmount &&
      approvedAmount >= increasedAmountToStake &&
      step === 0
    ) {
      setStep(1)
    }
  }, [approvedAmount, increasedAmountToStake, step, isApproveTxConfirmed])

  const selectedStake = useStake(stakeId)

  const newTotalStaked = useMemo(() => {
    if (!selectedStake) return 0n

    return selectedStake.amount + floatToBigInt(increasedAmount)
  }, [increasedAmount, selectedStake])

  const increasedAmountUsd = useBTCToUSDConversion(increasedAmount)
  const newTotalStakedUsd = useBTCToUSDConversion(bigIntToFloat(newTotalStaked))

  const veBTC = useEstimateVeBTCValue(
    floatToBigInt(increasedAmount),
    selectedStake?.lockDuration ?? STAKING_LOCK_DURATION.FOUR_MONTHS,
  )

  const txDetails: ReviewDetailsListItem[] = [
    {
      label: "Increase amount",
      endEnhancer: (
        <>
          <LabelMedium>{increasedAmount} BTC</LabelMedium>
          <ParagraphSmall color={theme.colors.contentSecondary}>
            $ {separateThousandsWithComma(increasedAmountUsd)}
          </ParagraphSmall>
        </>
      ),
    },
    {
      label: "New total staked",
      endEnhancer: (
        <>
          <LabelMedium>
            {bigIntToHumanReadableFormat(
              newTotalStaked,
              tokenDecimals,
              tokenDecimals,
            )}{" "}
            BTC
          </LabelMedium>
          <ParagraphSmall color={theme.colors.contentSecondary}>
            $ {separateThousandsWithComma(newTotalStakedUsd)}
          </ParagraphSmall>
        </>
      ),
    },
  ]

  const rewardsDetails: ReviewDetailsListItem[] = [
    {
      label: "Amount",
      endEnhancer: <StakingRewardsLabel veBTC={veBTC} />,
    },
  ]

  const onSubmitStep = async () => {
    if (approvedAmount! < increasedAmountToStake) {
      await approve(increasedAmountToStake)
      return
    }

    await increaseAmount({ stakeId, amount: increasedAmountToStake })
  }

  if (isIncreaseAmountTxPending || isWaitingForIncreaseAmountTxReceipt)
    return <WaitingForActionModalBody title="Waiting for transaction..." />

  if (isError)
    return (
      <FailedActionModalBody
        title="Something went wrong"
        description="Please try again."
        nextStepButtonLabel="Try again"
        onClickNextStep={onSubmitStep}
      />
    )

  return (
    <>
      <ModalBody
        className={css({
          height: "100%",
          display: "flex",
          flexDirection: "column",
          gap: theme.sizing.scale600,
        })}
      >
        <ReviewDetails label="Stake details" items={txDetails} />
        <ReviewDetails
          label="Rewards"
          items={rewardsDetails}
          backgroundColor={theme.colors.lime300}
        />
        <ProgressSteps currentStep={step}>
          <NumberedStep
            title={
              approvedAmount! >= increasedAmountToStake
                ? "BTC approved"
                : "Approve BTC"
            }
            isPending={
              isApprovalTxPending ||
              isFetchingApprovedAmount ||
              isWaitingForApproveTxReceipt
            }
            pendingText={
              isWaitingForApproveTxReceipt || isApprovalTxPending
                ? "Waiting for transaction..."
                : "Checking approval..."
            }
          />
          <NumberedStep title="Increase amount" />
        </ProgressSteps>
      </ModalBody>
      <ModalFooter
        className={css({
          display: "block",
        })}
      >
        <SignTransactionModalButton
          onClick={onSubmitStep}
          gasFee="$1.2"
          buttonLabel={`Sign on ${networkUtils.getMezoDAppLabel()}`}
        />
      </ModalFooter>
    </>
  )
}
